import accessoryBrakes from '@/assets/images/accessory-intro/brakes.png?trim&w=320&fit=inside&format=avif;webp&as=picture';
import accessoryWheels from '@/assets/images/accessory-intro/wheels.png?trim&w=320&fit=inside&format=avif;webp&as=picture';
import impreza from '@/assets/images/subaru-impreza.webp?trim&w=620;1000&fit=inside&format=avif;webp&as=picture';
import hilux from '@/assets/images/toyota-hilux-removebg-preview.webp?trim&format=avif;webp&as=picture';
import landCruiser from '@/assets/images/Toyota-Land-Cruiser-2024-removebg-preview.webp?trim&format=avif;webp&as=picture';
import noah from '@/assets/images/toyota-noah.webp?trim&format=avif;webp&as=picture';
import { type Picture } from '@/components/atoms/AppImage';
import { accessoryCategoryPhoto } from '@/config/accessoryPhotos';

/**
 * The cars that drift across the opening band of an inner page — the same
 * arrangement the home hero uses (config/heroVehicles.ts), at page-band scale.
 *
 * Every source is a cut-out on a transparent background, so `trim` drops the
 * empty padding and the car fills its slot instead of floating inside an empty
 * box. `fit=inside` goes with `trim` wherever a width is asked for: imagetools
 * sizes the output from the untrimmed aspect ratio, and its default `cover`
 * fit would crop what trimming has just tightened.
 *
 * Only the Impreza asks for widths. It is a 1000px source, so it can serve the
 * 620px slot at 1x and again at roughly 1.6x. The three Toyotas are 589–610px
 * once trimmed: a wider rendition would invent pixels, so they ship at their
 * own size and are never drawn above it (see INTRO_SLIDE_MAX_WIDTH and
 * hooks/usePageIntroSlides.ts). That cap is what keeps the band sharp on the
 * sources we have, and it is also what keeps the three cars roughly the same
 * size as they replace one another — a 1000px hatchback next to a 589px SUV
 * would make the loop read as a zoom rather than as a change of car.
 */
type IntroVehicle = {
  image: Picture;
  /** What the stage's caption says when the page does not override it. */
  label: string;
  /** Photos preserve their natural ratio; cut-outs sit directly on the stage. */
  kind: 'cutout' | 'photo';
};

export type IntroVehicleName = 'land-cruiser' | 'hilux' | 'noah' | 'impreza';

export const introVehicles: Record<IntroVehicleName, IntroVehicle> = {
  'land-cruiser': { image: landCruiser, label: 'Toyota Land Cruiser', kind: 'cutout' },
  hilux: { image: hilux, label: 'Toyota Hilux', kind: 'cutout' },
  noah: { image: noah, label: 'Toyota Noah', kind: 'cutout' },
  impreza: { image: impreza, label: 'Subaru Impreza', kind: 'cutout' },
};

/**
 * The widest the band draws a car, in CSS pixels. A car is capped at this or
 * at its own intrinsic width, whichever is smaller, so no source is stretched
 * past the detail it holds. 620 is the smallest number that all three default
 * slides can reach, which is why they sit within 5% of each other on screen.
 */
export const INTRO_SLIDE_MAX_WIDTH = 620;

/**
 * The three cars the band loops through unless a page names its own set.
 *
 * Ordered dark, light, light. The first car is the one the page opens on and
 * the only one in the prerendered HTML, and the Land Cruiser is both the most
 * aspirational of the three and the one whose silhouette reads most clearly at
 * a glance.
 */
export const DEFAULT_INTRO_SLIDES: readonly IntroVehicleName[] = [
  'land-cruiser',
  'hilux',
  'impreza',
];

/* -------------------------------------------------------------------------
 * Accessories
 * ---------------------------------------------------------------------- */

/**
 * Isolated accessory cut-outs and one cabin photograph for /accessories. The
 * transparent products sit directly on the black stage; the cabin image keeps
 * its natural photo treatment.
 */
export type IntroAccessoryName = 'wheels' | 'interior' | 'brakes';

const accessoryPhoto = (category: Parameters<typeof accessoryCategoryPhoto>[0]): Picture => {
  const picture = accessoryCategoryPhoto(category);
  if (!picture) throw new Error(`Missing accessory intro image for ${category}`);
  return picture;
};

export const introAccessories: Record<IntroAccessoryName, IntroVehicle> = {
  wheels: { image: accessoryWheels, label: 'Wheels & tyres', kind: 'cutout' },
  interior: { image: accessoryPhoto('interior'), label: 'Interior upgrades', kind: 'photo' },
  brakes: { image: accessoryBrakes, label: 'Brakes & performance', kind: 'cutout' },
};

/**
 * Everything the opening band can be asked to draw, cars and accessories
 * alike.
 *
 * One record for the slideshow to resolve. The `kind` only affects how an
 * image is presented; timing and responsive placement are shared.
 */
export type IntroMediaName = IntroVehicleName | IntroAccessoryName;

export const introMedia: Record<IntroMediaName, IntroVehicle> = {
  ...introVehicles,
  ...introAccessories,
};

/** The accessory categories used by the /accessories intro carousel. */
export const DEFAULT_ACCESSORY_SLIDES: readonly IntroAccessoryName[] = [
  'wheels',
  'interior',
  'brakes',
];
